import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  type CreateLotSizingFormOutput,
  type CreateLotSizingFormValues,
  createLotSizingFormSchema,
} from '@/features/purchase-planning/schema/create-lot-sizing-schema';
import { useCreateLotSizing } from '@/services/apis/mutations/lot-sizing/use-create-lot-sizing';
import { usePurchasePlanningFilters } from './use-purchase-planning-filters';
import { usePurchasePlanningWeek } from './use-purchase-planning-week';

export function useCreateLotSizingAction() {
  const { t } = useTranslation(['purchasePlanning', 'common']);
  const navigate = useNavigate();
  const { weekParams } = usePurchasePlanningWeek();
  const { filtersWithSupplier } = usePurchasePlanningFilters();
  const { mutate, isPending } = useCreateLotSizing();

  const { handleSubmit, formState, trigger, setValue } =
    useForm<CreateLotSizingFormValues>({
      resolver: zodResolver(createLotSizingFormSchema),
      defaultValues: {
        weekParams,
        filters: filtersWithSupplier,
      },
      mode: 'onChange',
    });

  useEffect(() => {
    setValue('weekParams', weekParams);
    setValue('filters', filtersWithSupplier);
    trigger();
  }, [weekParams, filtersWithSupplier, trigger, setValue]);

  const canCreate = formState.isValid;

  const onSubmit = (data: CreateLotSizingFormValues) => {
    mutate(data as CreateLotSizingFormOutput, {
      onSuccess: (response) => {
        navigate({ to: '/lot-sizing/$id', params: { id: response.id } });
      },
      onError: () => {
        toast.error(t('common:error'), {
          description: t('createLotSizingError', { ns: 'purchasePlanning' }),
        });
      },
    });
  };
  const validationErrorKey = useMemo(
    () =>
      [
        formState.errors.weekParams?.year?.message,
        formState.errors.weekParams?.week?.message,
        formState.errors.filters?.message,
      ].find((msg): msg is string => typeof msg === 'string'),
    [formState.errors],
  );

  return {
    handleSubmit,
    onSubmit,
    isPending,
    canCreate,
    validationErrorKey,
  };
}
